import EncryptedStorage from 'react-native-encrypted-storage';
import { AppState, Platform, type AppStateStatus } from 'react-native';
import { loadStoredAccess } from '../api/client';
import { API_BASE } from '../config';
import { randomUuid } from '../util/randomUuid';
// Версия — одна на отчёты об ошибках и аналитику: иначе десктоп в одной из них был бы «1.0.0».
import { appVersion } from './errorReporter';
import {
  durationBucketSeconds,
  type AnalyticsEventName,
  type AnalyticsProps,
} from './analyticsEvents';
import {
  appendEvent,
  dropSent,
  MAX_QUEUED_EVENTS,
  parseStoredQueue,
  takeBatch,
  type QueuedAnalyticsEvent,
} from './analyticsQueue';

/**
 * Продуктовая аналитика (FN-1.2): очередь на устройстве и отправка пачками на свой бэкенд.
 *
 * До этого модуля ни одна метрика из PRD не была измерима — ни время до первого сообщения, ни
 * DAU/MAU, ни воронка платежей. Регрессии в проекте находились полевым отчётом владельца, потому
 * что других глаз у продукта не было.
 *
 * Четыре правила, которым здесь подчинено всё остальное.
 *
 * 1. **Аналитика не ломает приложение.** Ни один путь отсюда не бросает наружу: `track()` не
 *    возвращает ошибок и не ждёт сети, отправка глотает всё. Метрика, из-за которой падает
 *    мессенджер, хуже отсутствующей метрики.
 * 2. **Аналитика не трогает сессию.** Отправка идёт голым `fetch`, а не через `apiFetch`: тот при
 *    401 без обновления токена вызывает `notifyAuthInvalid()` и выкидывает человека из аккаунта.
 *    Фоновая телеметрия не имеет права никого разлогинивать — при 401 пачка просто ждёт.
 * 3. **События не теряются на плохой связи.** Очередь переживает перезапуск приложения, а повтор
 *    пачки бесплатен: идентификатор события генерится здесь и на сервере он же первичный ключ.
 * 4. **В аналитику не попадает переписка.** Свойства события ограничены белым списком на сервере;
 *    здесь дополнительно нет ни одной точки, которая передавала бы текст, имя или заголовок.
 */

const QUEUE_KEY = 'vr_analytics_queue_v1';
const DEVICE_KEY = 'vr_analytics_device_v1';

/** Как часто пробуем отправить накопленное, пока приложение на экране. */
const FLUSH_INTERVAL_MS = 30_000;

/** Столько событий в очереди — повод не ждать таймера. */
const FLUSH_AT_QUEUE_SIZE = 20;

/** Запись в хранилище дороже, чем добавление в массив, поэтому она отложенная. */
const PERSIST_DEBOUNCE_MS = 2_000;

const REQUEST_TIMEOUT_MS = 15_000;

const isWeb = (): boolean => Platform.OS === 'web';

const isDev = typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV !== 'production';

/**
 * В отладочных сборках сбор выключен — иначе разработка и тестирование подмешиваются к продуктовым
 * числам, и «DAU» начинает включать человека, который двадцать раз перезапустил Metro.
 * `EXPO_PUBLIC_ANALYTICS_IN_DEV=1` включает обратно, когда нужно проверить сам сбор.
 */
const collectInDev =
  process.env.EXPO_PUBLIC_ANALYTICS_IN_DEV === '1' ||
  process.env.EXPO_PUBLIC_ANALYTICS_IN_DEV === 'true';

type SendOutcome = 'ok' | 'retry' | 'drop' | 'off';

let collecting = false;
let started = false;
let queue: QueuedAnalyticsEvent[] = [];
let sessionId = randomUuid();
let sessionStartedAtMs = Date.now();
let deviceId: string | null = null;
let flushing = false;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let appStateSub: { remove: () => void } | null = null;
let lastAppState: AppStateStatus = 'active';

function platformName(): 'ios' | 'android' | 'web' {
  // Сервер принимает ровно три платформы. Ничего другого мы не собираем и не выпускаем, а веб —
  // единственный, куда попадает всё, что не iOS и не Android.
  return Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'web';
}

async function storageGet(key: string): Promise<string | null> {
  try {
    if (isWeb()) {
      return globalThis.localStorage?.getItem(key) ?? null;
    }
    return await EncryptedStorage.getItem(key);
  } catch {
    return null;
  }
}

async function storageSet(key: string, value: string): Promise<void> {
  try {
    if (isWeb()) {
      globalThis.localStorage?.setItem(key, value);
      return;
    }
    await EncryptedStorage.setItem(key, value);
  } catch {
    /* очередь в памяти всё равно живёт — потеря записи не стоит исключения */
  }
}

function schedulePersist(): void {
  if (persistTimer) {
    return;
  }
  persistTimer = setTimeout(() => {
    persistTimer = null;
    void persistNow();
  }, PERSIST_DEBOUNCE_MS);
}

async function persistNow(): Promise<void> {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  await storageSet(QUEUE_KEY, JSON.stringify(queue));
}

async function ensureDeviceId(): Promise<string> {
  if (deviceId) {
    return deviceId;
  }
  const stored = await storageGet(DEVICE_KEY);
  // Формат сервера: 8–64 символа из [A-Za-z0-9._-]. UUID без дефисов помещается с запасом.
  if (stored && /^[A-Za-z0-9._-]{8,64}$/.test(stored)) {
    deviceId = stored;
    return stored;
  }
  const fresh = randomUuid().replace(/-/g, '');
  deviceId = fresh;
  await storageSet(DEVICE_KEY, fresh);
  return fresh;
}

/**
 * Записать событие. Не ждёт сети и никогда не бросает — вызывать можно откуда угодно, включая
 * обработчики нажатий.
 */
export function track(name: AnalyticsEventName, props?: AnalyticsProps): void {
  if (!collecting) {
    return;
  }
  try {
    queue = appendEvent(queue, {
      id: randomUuid(),
      sessionId,
      name,
      occurredAtMs: Date.now(),
      ...(props && Object.keys(props).length > 0 ? { props } : {}),
    });
    schedulePersist();
    if (queue.length >= FLUSH_AT_QUEUE_SIZE) {
      void flushAnalytics();
    }
  } catch {
    /* см. правило 1 в шапке модуля */
  }
}

async function sendBatch(batch: QueuedAnalyticsEvent[]): Promise<SendOutcome> {
  const token = await loadStoredAccess();
  if (!token) {
    // До входа события копятся: приёмник закрыт аутентификацией, и без токена мы не знаем, чьи они.
    return 'retry';
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        deviceId: await ensureDeviceId(),
        platform: platformName(),
        appVersion: appVersion(),
        events: batch,
      }),
      signal: controller.signal,
    });
    if (res.ok) {
      return 'ok';
    }
    if (res.status === 404) {
      // Сбор выключен на сервере — перестаём копить до перезапуска приложения.
      return 'off';
    }
    if (res.status === 401 || res.status === 408 || res.status === 429 || res.status >= 500) {
      return 'retry';
    }
    // Остальные 4xx пачка не переживёт никогда: повторять её значит застрять на ней навсегда и
    // потерять всё, что накопится следом.
    return 'drop';
  } catch {
    return 'retry';
  } finally {
    clearTimeout(timer);
  }
}

/** Отправить накопленное. Безопасно вызывать сколько угодно: параллельных отправок не будет. */
export async function flushAnalytics(): Promise<void> {
  if (!collecting || flushing || queue.length === 0) {
    return;
  }
  flushing = true;
  try {
    const batch = takeBatch(queue);
    const outcome = await sendBatch(batch);
    if (outcome === 'off') {
      collecting = false;
      queue = [];
      await persistNow();
      return;
    }
    if (outcome === 'ok' || outcome === 'drop') {
      queue = dropSent(queue, batch);
      await persistNow();
    }
  } catch {
    /* см. правило 1 в шапке модуля */
  } finally {
    flushing = false;
  }
}

function startSession(cold: boolean): void {
  sessionId = randomUuid();
  sessionStartedAtMs = Date.now();
  track('session_start', { cold });
}

function endSession(): void {
  track('session_end', {
    duration_bucket: durationBucketSeconds(Math.round((Date.now() - sessionStartedAtMs) / 1000)),
  });
}

function onAppStateChange(next: AppStateStatus): void {
  const previous = lastAppState;
  lastAppState = next;
  // Только `background`: на iOS `inactive` приходит на каждое движение шторки уведомлений, и
  // считать это концом сессии значило бы дробить одно пользование на десяток.
  if (next === 'background') {
    endSession();
    void flushAnalytics();
    void persistNow();
    return;
  }
  if (next === 'active' && previous === 'background') {
    startSession(false);
  }
}

/**
 * Поднять сбор. Вызывается один раз при старте приложения; повторный вызов ничего не делает.
 */
export function initAnalytics(): void {
  if (started) {
    return;
  }
  started = true;
  if (isDev && !collectInDev) {
    return;
  }
  collecting = true;
  // Синхронно, до чтения хранилища: иначе `track()` в первые миллисекунды после старта попадал бы
  // в сессию, у которой нет начала, — а именно с этих миллисекунд начинается холодный старт.
  startSession(true);
  void (async () => {
    try {
      // Дописываем, а не заменяем: пока читалось хранилище, в очереди уже могло что-то появиться.
      // Восстановленное старше, поэтому идёт первым.
      const restored = parseStoredQueue(await storageGet(QUEUE_KEY));
      if (restored.length > 0) {
        queue = [...restored, ...queue].slice(-MAX_QUEUED_EVENTS);
      }
      await ensureDeviceId();
      // Очередь с прошлого запуска уезжает сразу, а не через полминуты: чаще всего именно в ней
      // лежит последнее, что человек делал перед тем, как приложение закрылось или упало.
      void flushAnalytics();
    } catch {
      /* см. правило 1 в шапке модуля */
    }
  })();
  flushTimer = setInterval(() => {
    void flushAnalytics();
  }, FLUSH_INTERVAL_MS);
  lastAppState = AppState.currentState;
  appStateSub = AppState.addEventListener('change', onAppStateChange);
}

/**
 * Выход из аккаунта: отправить, что осталось, и начать с чистой очереди.
 *
 * Чистим обязательно. Приёмник берёт пользователя из токена, поэтому события, не уехавшие до
 * выхода, при следующем входе записались бы на другого человека — и «новый пользователь» получил
 * бы чужую историю в первый же день.
 */
export async function resetAnalyticsAfterLogout(): Promise<void> {
  if (!started) {
    return;
  }
  await flushAnalytics();
  queue = [];
  await persistNow();
  sessionId = randomUuid();
  sessionStartedAtMs = Date.now();
}

/** Остановить сбор — нужен тестам и полному завершению работы. */
export function stopAnalytics(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  appStateSub?.remove();
  appStateSub = null;
  collecting = false;
  started = false;
  queue = [];
}
